import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { makeRequest } from "../utils/request";
import { Text, Spinner } from "../components/base";

export const CliLoginPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(5);
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setError("Missing login token.");
      setStatus("error");
      return;
    }
    // Wait for user to be set (undefined means still loading)
    if (typeof user === "undefined") {
      return;
    }
    // Always attempt to complete CLI login if user is not undefined
    makeRequest("/v1/auth/cli-complete-login", {
      method: "POST",
      body: { token },
    })
      .then(() => {
        setStatus("success");
      })
      .catch((err) => {
        setError(err.message);
        setStatus("error");
      });
  }, [searchParams, user]);

  // Countdown effect for error state
  useEffect(() => {
    if (status === "error" && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (status === "error" && countdown === 0) {
      // Redirect to signin page when countdown reaches 0
      navigate(`/signin?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
  }, [status, countdown]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4">
            <Spinner className="text-brand dark:text-brand-light w-8 h-8" />
          </div>
          <Text variant="muted">Completing CLI login...</Text>
          <Text variant="muted">Please wait while we link your session to the CLI.</Text>
        </div>
      </div>
    );
  }
  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Text
            variant="success"
            className="font-semibold text-xl mb-4"
          >
            Success!
          </Text>
          <Text
            variant="muted"
            className="block"
          >
            You can now return to your CLI. This window can be closed.
          </Text>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Text
          variant="warning"
          className="font-semibold text-xl mb-4"
        >
          Not Signed In
        </Text>
        <Text
          variant="muted"
          className="block mb-4"
        >
          Redirecting to sign in page in {countdown} seconds...
        </Text>
      </div>
    </div>
  );
};
